import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import CloudQueueOutlinedIcon from '@material-ui/icons/CloudQueueOutlined';
import SettingsCard from 'components/kea/SettingsCard/SettingsCard';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { useCatalogFeedStatus } from 'pluginAdmin/features/catalogFeed/useCatalogFeedStatus';
import { useShellAppContext } from '../shell/context/useShellAppContext';
import DataSettingsToggle from './DataSettingsToggle';

const useStyles = makeStyles((theme) => ({
  card: {
    marginTop: theme.spacing(3),
  },
  layout: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
  },
  identity: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    minWidth: 0,
  },
  icon: {
    display: 'flex',
    width: 40,
    height: 40,
    flex: '0 0 auto',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    background: 'var(--kea-color-primary-soft)',
    color: 'var(--kea-color-primary)',
  },
  copy: {
    maxWidth: 650,
  },
  description: {
    marginTop: theme.spacing(0.5),
  },
  control: {
    display: 'flex',
    minHeight: 44,
    flex: '0 0 auto',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  controlLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    fontWeight: 600,
  },
  error: {
    marginTop: theme.spacing(2),
    '& .MuiAlert-action': {
      alignItems: 'center',
    },
  },
}));

export default function CatalogFeedAccessSection() {
  const classes = useStyles();
  const t = useLocalizedMessage();
  const { compatibility, bridgeClient } = useShellAppContext();
  const supported = compatibility?.catalogFeedAccess?.managed === true;
  const {
    api,
    state,
    setState,
    isLoading,
    error,
    setError,
    reload,
  } = useCatalogFeedStatus({ enabled: supported, bridgeClient });
  const [isSaving, setIsSaving] = useState(false);

  if (!supported) return null;

  const handleToggle = async (event) => {
    const enabled = event.target.checked;
    if (!state || isLoading || isSaving || error || (enabled && !state.configured)) return;

    setIsSaving(true);
    setError(null);
    try {
      setState(await api.setEnabled(enabled));
    } catch (nextError) {
      setError(nextError);
    } finally {
      setIsSaving(false);
    }
  };

  const disabled = isLoading || isSaving || !state || !state.configured || !!error;
  const enabled = state?.enabled === true;
  let statusKey = null;
  if (isLoading) statusKey = 'catalogFeed.dataSettings.status.loading';
  else if (!state && !error) statusKey = 'catalogFeed.dataSettings.status.unavailable';
  else if (state && !state.configured && !error) statusKey = 'catalogFeed.dataSettings.status.credentialsRequired';

  return (
    <SettingsCard className={classes.card} data-testid="catalog-feed-access-card">
      <Box className={classes.layout}>
        <Box className={classes.identity}>
          <Box
            className={classes.icon}
            aria-hidden="true"
          >
            {isLoading ? <CircularProgress size={20} color="inherit" /> : <CloudQueueOutlinedIcon />}
          </Box>
          <Box className={classes.copy}>
            <Typography variant="h6">
              {t('catalogFeed.dataSettings.title')}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.description}
            >
              {t('catalogFeed.dataSettings.description')}
            </Typography>
          </Box>
        </Box>

        <Box className={classes.control}>
          {statusKey && (
            <span className={classes.controlLabel} id="catalog-feed-toggle-status" aria-live="polite">
              {t(statusKey)}
            </span>
          )}
          <DataSettingsToggle
            checked={enabled}
            onChange={handleToggle}
            disabled={disabled}
            label={t('catalogFeed.dataSettings.toggle')}
            describedBy={statusKey ? 'catalog-feed-toggle-status' : undefined}
            testId="catalog-feed-toggle"
          />
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className={classes.error}
          action={(
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setError(null);
                reload();
              }}
            >
              {t('catalogFeed.dataSettings.retry')}
            </Button>
          )}
        >
          {t('catalogFeed.dataSettings.error')}
        </Alert>
      )}
    </SettingsCard>
  );
}
